// /src/pages/VendorList.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  FaPencilAlt,
  FaTrashAlt,
  FaFilter,
  FaTimesCircle,
  FaPowerOff,
} from "react-icons/fa";
import { FiRotateCcw } from "react-icons/fi";
import { useDebounce } from "../hooks/useDebounced";
import {
  useGetAllVendorsQuery,
  useActivateVendorMutation,
  useDeactivateVendorMutation,
  useDeleteVendorMutation,
  useBulkActivateVendorsMutation,
  useBulkDeactivateVendorsMutation,
  useBulkDeleteVendorsMutation,
} from "../toolkitServices/vendorApi";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import Select from "react-select";

/* ------------ Tiny spinner ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

const headerCell =
  "px-3 sm:px-4 py-1.5 text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide";
const dataCell = "px-3 sm:px-4 py-1.5 text-xs sm:text-sm";

const paginationSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    borderRadius: 6,
    borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
    boxShadow: state.isFocused
      ? "0 0 0 1px rgba(148,129,206,.25)"
      : "none",
    fontSize: 10,
  }),
  valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
  menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
  option: (base, state) => ({
    ...base,
    padding: "6px 8px",
    backgroundColor: state.isSelected
      ? "#9481ce"
      : state.isFocused
        ? "#ede9fe"
        : "white",
    color: state.isSelected ? "white" : "#334155",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

/* ------------- skeleton rows ------------- */
const SkeletonRows = ({ cols = 9, rows = 8 }) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <tr key={`sk-${r}`} className="animate-pulse">
        {Array.from({ length: cols }).map((__, c) => (
          <td key={`sk-${r}-${c}`} className={dataCell}>
            <div className="h-3 w-20 bg-slate-200 rounded" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const canDelete = (user) => {
  const perm = user?.permissions?.find((p) => p.moduleKey === "quotation");
  return user?.role === "owner" || perm?.permissions?.delete;
};

const VendorList = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  // search + debounce
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);

  // pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // 🔹 main filters (ab yahin se status jayega API me)
  const [filters, setFilters] = useState({
    status: "", // "", "Active", "Inactive"
  });

  // filter modal ke liye temp state
  const [showFilters, setShowFilters] = useState(false);
  const [tempFilters, setTempFilters] = useState(filters);

  // 👉 filter modal ke first control ke liye ref (Status select)
  const statusRef = useRef(null);

  const openFilters = () => {
    setTempFilters(filters);
    setShowFilters(true);
  };

  // modal open hote hi Status select pe focus
  useEffect(() => {
    if (!showFilters) return;
    const t = setTimeout(() => {
      if (statusRef.current && typeof statusRef.current.focus === "function") {
        statusRef.current.focus();
      }
    }, 80);
    return () => clearTimeout(t);
  }, [showFilters]);

  const activeParam = useMemo(() => {
    if (filters.status === "Active") return true;
    if (filters.status === "Inactive") return false;
    return undefined;
  }, [filters.status]);

  const { data, isLoading, isFetching, isError, error } = useGetAllVendorsQuery({
    page,
    limit,
    search: debouncedSearch || "",
    active: activeParam,
  });

  const [activateVendor] = useActivateVendorMutation();
  const [deactivateVendor] = useDeactivateVendorMutation();
  const [deleteVendor] = useDeleteVendorMutation();

  const [bulkActivate] = useBulkActivateVendorsMutation();
  const [bulkDeactivate] = useBulkDeactivateVendorsMutation();
  const [bulkDelete] = useBulkDeleteVendorsMutation();

  const rows = data?.data || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;

  // selection state
  const [selected, setSelected] = useState(() => new Set());
  const allIdsOnPage = useMemo(() => rows.map((r) => r._id), [rows]);
  const allSelected =
    allIdsOnPage.length > 0 && allIdsOnPage.every((id) => selected.has(id));
  const hasSelection = selected.size > 0;

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        allIdsOnPage.forEach((id) => next.delete(id));
      } else {
        allIdsOnPage.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  // shortcuts: Ctrl + P
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && String(e.key).toLowerCase() === "p") {
        e.preventDefault();
        navigate("/members/vendor-list/create-vendor");
        return;
      }

      if (e.ctrlKey && (e.key === "=" || e.key === "+" || e.code === "NumpadAdd")) {
        e.preventDefault();
        navigate("/members/vendor-list/create-vendor");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  const onToggleActive = async (row) => {
    try {
      const isActive = !!row.isActive;
      if (isActive) await deactivateVendor(row._id).unwrap();
      else await activateVendor(row._id).unwrap();
    } catch (e) {
      alert(e?.data?.msg || "Action failed");
      console.error(e);
    }
  };

  const onDeleteOne = async (id) => {
    if (!window.confirm("Delete this vendor?")) return;
    try {
      await deleteVendor(id).unwrap();
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (e) {
      alert(e?.data?.msg || "Delete failed");
      console.error(e);
    }
  };

  // bulk handlers
  const runBulk = async (fn) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      await fn(ids).unwrap();
      setSelected(new Set());
    } catch (e) {
      alert(e?.data?.msg || "Bulk action failed");
      console.error(e);
    }
  };

  const renderStatusPill = (row) => {
    const isActive = !!row.isActive;
    return (
      <span
        className={[
          "text-[11px] px-2 py-0.5 rounded-full",
          isActive ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700",
        ].join(" ")}
      >
        {isActive ? "Active" : "Inactive"}
      </span>
    );
  };

  /* ---------- chips for search + status ---------- */
  const chips = useMemo(() => {
    const list = [];
    if (filters.status) list.push({ key: "status", label: `Status: ${filters.status}` });
    if (search) list.push({ key: "search", label: `Search: ${search}` });
    return list;
  }, [filters, search]);

  const clearChip = (key) => {
    if (key === "status") {
      setFilters((f) => ({ ...f, status: "" }));
    } else if (key === "search") {
      setSearch("");
    }
    setPage(1);
  };

  const clearAllChips = () => {
    setFilters({ status: "" });
    setSearch("");
    setPage(1);
  };

  // filter/search/limit change => page 1
  useEffect(() => {
    const t = setTimeout(() => setPage(1), 300);
    return () => clearTimeout(t);
  }, [debouncedSearch, filters, limit]);

  return (
    <div className="relative flex h-[calc(100vh-140px)] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg md:h-[calc(100vh-70px)]">
      {/* Global loader overlay (first load) */}
      {isFetching && !rows.length && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/60">
          <div className="flex flex-col items-center gap-2 text-[#6b59c6] text-xs sm:text-sm">
            <Spinner size={26} />
            <span>Loading vendors…</span>
          </div>
        </div>
      )}

      {/* Header – gradient same as others */}
      <div className="flex shrink-0 items-center justify-between gap-3 bg-[#9481ce] px-3 py-2.5 text-white shadow-sm sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold tracking-tight sm:text-lg">
              Vendors
            </h2>
            <p className="truncate text-[9px] text-violet-100 sm:text-[11px]">
              Manage vendor profiles and account status
            </p>
          </div>
          {isFetching && rows.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/10">
              <Spinner size={10} /> refreshing…
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span className="hidden rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-medium ring-1 ring-white/10 lg:inline-flex">
            {total || 0} vendor{total === 1 ? "" : "s"}
          </span>
          <Link
            to="/members/vendor-list/create-vendor"
            className="inline-flex h-9 items-center gap-1 rounded-xl bg-white px-3 text-[11px] font-semibold text-[#6b4fd8] shadow-sm transition hover:bg-violet-50 sm:rounded-full sm:px-4"
          >
            + Add New
          </Link>
        </div>
      </div>

      {/* Filters bar – ab yaha sirf search + Filters button, status modal me */}
      <div className="shrink-0 border-b bg-slate-50/80 px-3 py-2 sm:px-4">
        <div className="flex flex-wrap items-end gap-3">
          {/* Search */}
          <div className="flex flex-col gap-[1px] flex-1 min-w-[180px]">
            <label className="text-[11px] font-medium text-slate-600">Search</label>
            <input
              type="text"
              placeholder="Vendor, company, contact…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 px-3 py-1.5 rounded-lg text-[11px] text-gray-800 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-400"
            />
          </div>

          {/* Filters button (opens modal) */}
          <div className="flex flex-col gap-[1px] w-auto">
            <label className="text-[11px] font-medium text-slate-600 invisible">
              Filters
            </label>
            <button
              type="button"
              onClick={openFilters}
              className="inline-flex items-center gap-1 bg-white text-[#6b59c6] px-3 py-1.5 rounded-full text-[11px] border border-violet-200 hover:bg-violet-50 outline-[#6b59c6]"
              title="Advanced filters"
            >
              <FaFilter /> Filters
            </button>
          </div>
        </div>
      </div>

      {/* Chips row (search + status) */}
      {chips.length > 0 && (
        <div className="px-4 py-2 border-b bg-slate-50 flex flex-wrap items-center gap-2">
          {chips.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-2 text-[11px] bg-white border px-2 py-1 rounded-full shadow-sm"
            >
              {c.label}
              <button
                className="text-slate-500 hover:text-red-500"
                onClick={() => clearChip(c.key)}
                title="Clear"
              >
                <FaTimesCircle />
              </button>
            </span>
          ))}
          <button
            className="ml-auto text-[11px] text-[#6b59c6] hover:underline"
            onClick={clearAllChips}
            title="Clear all"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Bulk action bar */}
      {hasSelection && (
        <div className="flex items-center justify-between px-4 py-2 border-b bg-amber-50">
          <span className="text-[11px] sm:text-sm text-amber-900">
            Selected {selected.size} vendor{selected.size > 1 ? "s" : ""}
          </span>
          <div className="flex gap-2">
            <button
              className="px-3 py-1 text-[11px] rounded border border-emerald-500 text-emerald-700 hover:bg-emerald-50"
              onClick={() => runBulk(bulkActivate)}
            >
              Activate
            </button>
            <button
              className="px-3 py-1 text-[11px] rounded border border-amber-500 text-amber-700 hover:bg-amber-50"
              onClick={() => runBulk(bulkDeactivate)}
            >
              Deactivate
            </button>
            <button
              className="px-3 py-1 text-[11px] rounded border border-rose-500 text-rose-600 hover:bg-rose-50"
              onClick={async () => {
                if (!window.confirm(`Delete ${selected.size} vendor(s)?`)) return;
                await runBulk(bulkDelete);
              }}
            >
              Delete
            </button>
          </div>
        </div>
      )}

      {/* Table / States */}
      <div
        className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto overscroll-x-contain bg-white [-webkit-overflow-scrolling:touch]"
        aria-busy={isFetching}
      >
        {isError ? (
          <div className="py-10 text-center text-red-500 text-sm">
            Failed to load vendors
            {error?.data?.msg ? `: ${error.data.msg}` : ""}
          </div>
        ) : (
          <table className="w-full min-w-[900px] text-left text-xs sm:text-sm">
            <thead className="sticky top-0 z-40 bg-[#efeafd] text-[#4b3d91] shadow-sm">
              <tr>
                <th className={headerCell}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="h-3.5 w-3.5"
                    title="Select all on page"
                  />
                </th>
                <th className={headerCell}>#</th>
                <th className={headerCell}>Vendor</th>
                <th className={headerCell}>Company</th>
                <th className={headerCell}>Contact</th>
                <th className={headerCell}>Type</th>
                <th className={headerCell}>Location</th>
                <th className={headerCell}>Status</th>
                <th className={`${headerCell} sticky right-0 top-0 z-50 w-[82px] min-w-[82px] border-l border-violet-200 bg-[#ddd5f7] text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.45)] sm:w-[104px] sm:min-w-[104px]`}><span className="sm:hidden">Act</span><span className="hidden sm:inline">Action</span></th>
              </tr>
            </thead>
            <tbody className="bg-white text-gray-700">
              <AnimatePresence initial={false}>
                {isFetching && rows.length ? (
                  <SkeletonRows cols={9} rows={8} />
                ) : rows.length ? (
                  rows.map((item, index) => {
                    const isChecked = selected.has(item._id);
                    return (
                      <motion.tr
                        key={item._id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2, delay: index * 0.02 }}
                        className={`group border-t transition hover:bg-[#f4f0ff] ${
                          isChecked ? "bg-violet-50" : ""
                        }`}
                      >
                        <td className={dataCell}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleOne(item._id)}
                            className="h-3.5 w-3.5"
                          />
                        </td>
                        <td className={`${dataCell} font-medium`}>
                          {(page - 1) * limit + index + 1}
                        </td>
                        <td className={dataCell}>{item.vendorName || "-"}</td>
                        <td className={dataCell}>{item.companyName || "-"}</td>
                        <td className={dataCell}>{item.contact || "-"}</td>
                        <td className={dataCell}>
                          {(item.vendorType || "-")
                            .toString()
                            .replace(/_/g, " ")
                            .toLowerCase()
                            .replace(/^\w/, (c) => c.toUpperCase())}
                        </td>
                        <td className={dataCell}>
                          {[item.city, item.state].filter(Boolean).join(", ") || "-"}
                        </td>
                        <td className={dataCell}>{renderStatusPill(item)}</td>

                        <td className={`${dataCell} sticky right-0 z-10 w-[82px] min-w-[82px] whitespace-nowrap border-l border-violet-100 bg-violet-50 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.35)] transition group-hover:bg-violet-100 sm:w-[104px] sm:min-w-[104px]`}>
                          <div className="inline-flex items-center justify-center gap-0.5 sm:gap-1">
                            {/* Edit */}
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-[#6b59c6] hover:bg-violet-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                              title="Edit vendor"
                              onClick={() =>
                                navigate(`/members/vendor-list/${item._id}/edit`)
                              }
                            >
                              <FaPencilAlt />
                            </motion.button>

                            {/* Delete */}
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="order-2 inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-rose-500 hover:bg-rose-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                              title="Delete vendor"
                              onClick={() =>{
                                if(canDelete(user)){
                                  onDeleteOne(item._id)
                                }else{
                                  toast.error(
                                  "You don't have permission to delete",
                                );
                                }
                              } 
                            }

                            >
                              <FaTrashAlt />
                            </motion.button>

                            {/* Activate / Deactivate – icon only */}
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className={`order-1 inline-flex h-6 w-6 items-center justify-center rounded-md border text-[10px] sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px] ${
                                item.isActive
                                  ? "border-amber-100 text-amber-600 hover:bg-amber-50"
                                  : "border-emerald-100 text-emerald-600 hover:bg-emerald-50"
                              }`}
                              onClick={() => onToggleActive(item)}
                              title={
                                item.isActive ? "Deactivate vendor" : "Activate vendor"
                              }
                            >
                              <FaPowerOff />
                            </motion.button>
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })
                ) : (
                  !isLoading &&
                  !isFetching && (
                    <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <td
                        colSpan={9}
                        className="px-4 py-8 text-center text-xs text-slate-500"
                      >
                        No matching vendor records found. Try changing filters or
                        search.
                      </td>
                    </motion.tr>
                  )
                )}
              </AnimatePresence>
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="shrink-0 flex items-center justify-between px-2 py-1.5 sm:px-4 sm:py-2 border-t text-[10px] sm:text-sm bg-slate-50/60 rounded-b-2xl gap-2">
        <div className="hidden text-slate-600 sm:block">
            Showing{" "}
            <span className="font-semibold">
              {rows.length ? (page - 1) * limit + 1 : 0}
            </span>{" "}
            to{" "}
            <span className="font-semibold">
              {rows.length ? (page - 1) * limit + rows.length : 0}
            </span>{" "}
            of <span className="font-semibold">{total}</span> vendors
        </div>

        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
          <div className="flex shrink-0 items-center gap-1 text-slate-600">
            <span className="hidden text-xs sm:inline">Rows / page</span>
            <Select
              value={{ value: limit, label: String(limit) }}
              options={[10, 20, 50, 100].map((number) => ({
                value: number,
                label: String(number),
              }))}
              onChange={(option) => {
                setLimit(Number(option?.value) || 10);
                setPage(1);
              }}
              isDisabled={isFetching || isLoading}
              isSearchable={false}
              menuPlacement="top"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              styles={paginationSelectStyles}
              className="w-[54px]"
              aria-label="Rows per page"
            />
            <span className="text-[9px] text-slate-400 sm:hidden">/ page</span>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:flex-none sm:justify-start sm:gap-2">
            <button
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base hover:bg-white disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || isFetching || isLoading}
              aria-label="Previous page"
            >
              <span className="sm:hidden" aria-hidden="true">&lsaquo;</span>
              <span className="hidden sm:inline">Prev</span>
            </button>

            <span className="flex items-center gap-1 text-slate-600 sm:hidden">
              <Select
                value={{
                  value: pages ? page : 1,
                  label: String(pages ? page : 1),
                }}
                options={Array.from(
                  { length: Math.max(1, pages) },
                  (_, index) => ({ value: index + 1, label: String(index + 1) }),
                )}
                onChange={(option) => setPage(Number(option?.value) || 1)}
                isDisabled={isFetching || isLoading}
                isSearchable={false}
                menuPlacement="top"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                menuShouldScrollIntoView={false}
                styles={paginationSelectStyles}
                className="w-[52px]"
                aria-label="Select page number"
              />
              <span>/ {Math.max(1, pages)}</span>
            </span>
            <span className="hidden items-center gap-1 text-slate-600 sm:flex">
              <span>Page</span>
              <Select value={{ value: pages ? page : 1, label: String(pages ? page : 1) }} options={Array.from({ length: Math.max(1, pages) }, (_, index) => ({ value: index + 1, label: String(index + 1) }))} onChange={(option) => setPage(Number(option?.value) || 1)} isDisabled={isFetching || isLoading} isSearchable={false} menuPlacement="top" menuPosition="fixed" menuPortalTarget={document.body} menuShouldScrollIntoView={false} styles={paginationSelectStyles} className="w-[58px]" aria-label="Select page number" />
              <span>of {Math.max(1, pages)}</span>
            </span>

            <button
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base hover:bg-white disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
              onClick={() =>
                setPage((p) => (pages ? Math.min(pages, p + 1) : p + 1))
              }
              disabled={page >= pages || isFetching || isLoading}
              aria-label="Next page"
            >
              <span className="sm:hidden" aria-hidden="true">&rsaquo;</span>
              <span className="hidden sm:inline">Next</span>
            </button>
          </div>
        </div>
      </div>

      {/* FILTER MODAL – yaha ab Status aa gaya (bahar se hata diya) */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            className="fixed inset-0 z-[9998] flex items-end justify-center bg-slate-950/40 p-3 backdrop-blur-[1px] sm:items-center sm:p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowFilters(false)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 10, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: 10, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-center justify-between bg-gradient-to-r from-[#6b59c6] to-[#9481ce] px-4 py-3 text-white">
                <div>
                  <h3 className="text-sm font-semibold">
                    Vendor filters
                  </h3>
                  <p className="mt-0.5 text-[10px] text-violet-100">
                    Filter vendors by status.
                  </p>
                </div>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/15 transition hover:bg-white/25"
                  onClick={() => setShowFilters(false)}
                  aria-label="Close filters"
                >
                  <FaTimesCircle />
                </button>
              </div>

              <div className="p-4 overflow-y-auto space-y-3 text-[11px]">
                <div>
                  <label className="block text-slate-600 mb-1">Status</label>
                  <select
                    ref={statusRef} // 👈 auto-focus yahi pe
                    value={tempFilters.status}
                    onChange={(e) =>
                      setTempFilters((f) => ({
                        ...f,
                        status: e.target.value,
                      }))
                    }
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-[11px] text-slate-700 outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-100"
                  >
                    <option value="">All</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setTempFilters({ status: "" })}
                  className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-800"
                >
                  <FiRotateCcw />
                  Reset
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowFilters(false)}
                    className="px-3 py-1.5 rounded-md border border-slate-200 text-[11px] text-slate-700 bg-white hover:bg-slate-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setFilters(tempFilters);
                      setPage(1);
                      setShowFilters(false);
                    }}
                    className="rounded-full bg-[#6b59c6] px-5 py-2 text-[11px] font-semibold text-white shadow-sm transition hover:bg-[#5948ae]"
                  >
                    Apply filters
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VendorList;
